import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowClockwise,
  CaretDown,
  CaretUp,
  CheckCircle,
  CircleNotch,
  FileText,
  Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { toastError } from "@/lib/userMessages";
import { isNoConfiguredProviderResponse, NoConfiguredProviderError } from "@/lib/edgeFnError";
import { useSession } from "@clerk/clerk-react";

import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ConsultaAnalysisView } from "@/components/shared/ConsultaAnalysisView";
import { useConsultasAI } from "@/features/consultas/hooks/useConsultasAI";
import { useDeleteConsulta } from "@/features/consultas/hooks/useDeleteConsulta";
import { useRenameConsulta } from "@/features/consultas/hooks/useRenameConsulta";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { InlineEditText } from "@/components/shared/InlineEditText";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCreateNotification } from "@/features/notifications/hooks/useCreateNotification";

function StatusBadge({
  status,
  failedStage,
  hasAnalysisError,
}: {
  status?: string;
  failedStage?: string | null;
  hasAnalysisError?: boolean;
}) {
  if (!status || status === "running" || status === "pending") {
    return (
      <Badge variant="secondary" className="gap-1 shrink-0">
        <CircleNotch className="w-3 h-3 animate-spin" aria-hidden="true" />
        Processando
      </Badge>
    );
  }
  // A11-UX: done + embedded error → partial (offer retry, not full failure).
  if (status === "done" && hasAnalysisError) {
    return (
      <Badge className="bg-warning text-warning-foreground gap-1 shrink-0">
        <Warning className="w-3 h-3" aria-hidden="true" />
        Parcial
      </Badge>
    );
  }
  if (status === "done") {
    return (
      <Badge className="bg-primary text-primary-foreground gap-1 shrink-0">
        <CheckCircle className="w-3 h-3" aria-hidden="true" />
        Concluída
      </Badge>
    );
  }
  return (
    <Badge variant="destructive" className="gap-1 shrink-0">
      <Warning className="w-3 h-3" aria-hidden="true" />
      {failedStage === "analysis" ? "Falhou (análise)" : "Falhou"}
    </Badge>
  );
}

export default function ConsultaDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { session } = useSession();
  const consultasQuery = useConsultasAI();
  const createNotification = useCreateNotification();
  const deleteConsulta = useDeleteConsulta();
  const renameConsulta = useRenameConsulta();

  const consulta = consultasQuery.data?.find((c) => c.id === id) ?? null;

  usePageTitle(consulta?.label ?? "Análise");

  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [retryingJobId, setRetryingJobId] = useState<string | null>(null);

  const isProcessing =
    consulta?.jobStatus === "running" || consulta?.jobStatus === "pending";
  const analysisHasError =
    consulta?.analysisPayload &&
    typeof consulta.analysisPayload === "object" &&
    "erro" in (consulta.analysisPayload as Record<string, unknown>);

  const canRetry =
    consulta?.aiJobId &&
    (
      (consulta.jobStatus === "failed" && consulta.jobFailedAtStage === "analysis") ||
      (consulta.jobStatus === "done" && analysisHasError)
    );

  // Poll while job is in-progress
  useEffect(() => {
    if (!isProcessing) return;
    const interval = setInterval(() => void consultasQuery.refetch(), 3000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProcessing]);

  const handleRetry = async (jobId: string) => {
    setRetryingJobId(jobId);
    try {
      const token = await session?.getToken();
      if (!token) throw new Error("Não autenticado");
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string).replace(/\/$/, "");
      const resp = await fetch(`${supabaseUrl}/functions/v1/llm-proxy/retry-analysis`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ job_id: jobId }),
      });
      if (!resp.ok) {
        const errBody = (await resp.json().catch(() => ({}))) as {
          error?: string;
          detail?: string;
          code?: string;
        };
        if (isNoConfiguredProviderResponse(resp.status, errBody)) {
          throw new NoConfiguredProviderError(errBody.detail ?? errBody.error ?? "no_configured_provider", {
            status: resp.status,
            code: errBody.error,
          });
        }
        throw new Error(errBody.error ?? `Erro HTTP ${resp.status}`);
      }
      toast.success("Análise reprocessada com sucesso.");
      void consultasQuery.refetch();
      void createNotification({
        type: "job_done",
        title: "Análise de consulta concluída",
        body: consulta?.label ?? undefined,
        href: `/consultas/${id ?? ""}`,
        metadata: { job_id: jobId },
      });
    } catch (err) {
      toastError(err, "Não foi possível retentar a análise. Tente novamente.");
    } finally {
      setRetryingJobId(null);
    }
  };

  return (
    <AppLayout>
      <div className="w-full p-4 sm:p-6 pb-16 space-y-6 animate-fade-in">
        {/* Back */}
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5 -ml-2 text-muted-foreground"
          onClick={() => navigate("/consultas?tab=analises")}
        >
          <ArrowLeft className="w-4 h-4" />
          Análises
        </Button>

        {/* Loading skeleton */}
        {consultasQuery.isPending && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-7 w-72" />
              <Skeleton className="h-4 w-36" />
            </div>
            <Skeleton className="h-24 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
            <Skeleton className="h-32 w-full rounded-xl" />
          </div>
        )}

        {/* Not found */}
        {!consultasQuery.isPending && !consulta && (
          <div className="flex flex-col items-center justify-center py-20 gap-3 text-muted-foreground">
            <Warning weight="duotone" className="w-10 h-10" />
            <p className="font-medium">Análise não encontrada.</p>
            <Button variant="outline" size="sm" onClick={() => navigate("/consultas?tab=analises")}>
              Voltar para Análises
            </Button>
          </div>
        )}

        {consulta && (
          <>
            {/* Header */}
            <div className="space-y-1">
              <div className="flex items-start justify-between gap-3">
                <h1 className="text-xl font-semibold text-foreground leading-tight min-w-0">
                  <InlineEditText
                    value={consulta.label}
                    onSave={async (newName) => {
                      try {
                        await renameConsulta.mutateAsync({ id: consulta.id, label: newName });
                        toast.success("Renomeado com sucesso.");
                      } catch (err) {
                        toastError(err, "Não foi possível renomear. Tente novamente.");
                        throw err;
                      }
                    }}
                    placeholder="Consulta sem nome"
                  />
                </h1>
                <div className="flex items-center gap-2 shrink-0">
                  <DeleteEntityButton
                    entityLabel="consulta"
                    confirmDescription="A consulta e sua análise serão removidas permanentemente."
                    onConfirm={async () => {
                      try {
                        await deleteConsulta.mutateAsync(consulta.id);
                        toast.success("Consulta excluída.");
                        navigate("/consultas");
                      } catch (err) {
                        toastError(err, "Não foi possível excluir. Tente novamente.");
                        throw err;
                      }
                    }}
                    data-testid="consulta-detail-delete"
                  />
                  <StatusBadge
                    status={consulta.jobStatus}
                    failedStage={consulta.jobFailedAtStage}
                    hasAnalysisError={Boolean(analysisHasError)}
                  />
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                {new Date(consulta.createdAt).toLocaleString("pt-BR")}
              </p>
            </div>

            {/* Processing */}
            {isProcessing && (
              <div className="flex items-center gap-4 p-5 rounded-xl bg-muted/30 border border-border/50">
                <CircleNotch className="w-6 h-6 animate-spin text-primary shrink-0" />
                <div>
                  <p className="font-medium text-foreground">Processando análise…</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Transcrevendo o áudio e gerando insights. Geralmente 1-2 min por 10 min de áudio.
                  </p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Pode fechar a página — avisaremos por notificação quando terminar.
                  </p>
                </div>
              </div>
            )}

            {/* Insights — main focus */}
            {consulta.analysisPayload && (
              <ConsultaAnalysisView analysis={consulta.analysisPayload} />
            )}

            {!consulta.analysisPayload && !isProcessing && consulta.jobStatus !== "failed" && (
              <p className="text-sm text-muted-foreground italic text-center py-8">
                Nenhuma análise disponível.
              </p>
            )}

            {/* Retry */}
            {canRetry && (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                disabled={retryingJobId === consulta.aiJobId}
                onClick={() => handleRetry(consulta.aiJobId!)}
              >
                {retryingJobId === consulta.aiJobId ? (
                  <CircleNotch className="w-4 h-4 animate-spin" />
                ) : (
                  <ArrowClockwise className="w-4 h-4" />
                )}
                Retentar análise
              </Button>
            )}

            {/* Transcript — collapsed at bottom */}
            {consulta.transcriptText && (
              <div className="border border-border/50 rounded-xl overflow-hidden mt-4">
                <button
                  type="button"
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-muted/30 transition-colors"
                  onClick={() => setTranscriptOpen((o) => !o)}
                  aria-expanded={transcriptOpen}
                >
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                    <span className="text-sm font-medium text-muted-foreground">Transcrição</span>
                  </div>
                  {transcriptOpen ? (
                    <CaretUp className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  ) : (
                    <CaretDown className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                  )}
                </button>
                {transcriptOpen && (
                  <div className="px-4 pb-5 border-t border-border/50">
                    <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed pt-4">
                      {consulta.transcriptText}
                    </p>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </AppLayout>
  );
}
